import { NavLink } from 'react-router';
import { useTranslations } from 'use-intl';
import { Ellipsis } from 'lucide-react';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import { TABS } from '@/lib/nav';
import { NAV_ICONS } from './icons';

/**
 * The phone's navigation: four destinations and a way to everything else.
 *
 * At the bottom because the phone is held one-handed at the side of a pitch and
 * the top of a six-inch screen is a reach. Fifty-six pixels tall and padded by
 * the home-indicator inset, because the previous nav was thirty-eight pixels
 * and sat above a bar nobody could see the end of.
 *
 * The signal ochre marks the current tab, which is the one thing it is for in
 * chrome — "now" — so it does not borrow from the load ramp.
 */
export function BottomTabs({
  locale,
  onMore,
  moreOpen,
}: {
  locale: Locale;
  onMore: () => void;
  moreOpen: boolean;
}) {
  const t = useTranslations();

  return (
    <nav
      aria-label={t('nav.primary')}
      className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 backdrop-blur lg:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <ul className="mx-auto flex max-w-2xl">
        {TABS.map((item) => {
          const Icon = NAV_ICONS[item.key];
          return (
          <li key={item.key} className="flex-1">
            <NavLink
              to={localePath(locale, item.to)}
              end={item.end}
              className={({ isActive }) =>
                `flex min-h-14 flex-col items-center justify-center gap-1 px-1 py-2 transition-colors ${
                  isActive && !moreOpen ? 'text-ink' : 'text-ink-muted'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={`grid h-6 w-10 place-items-center rounded-(--radius-control) ${
                      isActive && !moreOpen ? 'bg-signal-soft' : ''
                    }`}
                  >
                    <Icon aria-hidden="true" className="size-5" />
                  </span>
                  <span
                    className={`text-center text-[0.6875rem] leading-tight ${
                      isActive && !moreOpen ? 'font-semibold' : ''
                    }`}
                  >
                    {t(`nav.${item.key}`)}
                  </span>
                </>
              )}
            </NavLink>
          </li>
          );
        })}

        <li className="flex-1">
          <button
            type="button"
            onClick={onMore}
            aria-expanded={moreOpen}
            className={`flex min-h-14 w-full flex-col items-center justify-center gap-1 px-1 py-2 transition-colors ${
              moreOpen ? 'text-ink' : 'text-ink-muted'
            }`}
          >
            <span
              className={`grid h-6 w-10 place-items-center rounded-(--radius-control) ${
                moreOpen ? 'bg-signal-soft' : ''
              }`}
            >
              <Ellipsis aria-hidden="true" className="size-5" />
            </span>
            <span
              className={`text-center text-[0.6875rem] leading-tight ${moreOpen ? 'font-semibold' : ''}`}
            >
              {t('nav.more')}
            </span>
          </button>
        </li>
      </ul>
    </nav>
  );
}
